import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Sparkles, ArrowRight } from 'lucide-react';
import { PageHero } from '@/components/PageHero';
import { CTASection } from '@/components/CTASection';
import { Reveal } from '@/components/Reveal';
import { Skeleton } from '@/components/ui/skeleton';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { SEO } from '@/components/SEO';
import { getServiceBySlug } from '@/lib/api';
import { getIcon } from '@/lib/iconMap';
import { AI_FEATURES_ENABLED } from '@/lib/featureFlags';
import { SERVICE_DETAIL_PAGE, INTELLIGENCE_ENTRY } from '@/constants/testIds';
import NotFound from '@/pages/NotFound';
import { keyedList } from '@/lib/keys';

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setService(null);
    setNotFound(false);

    const load = (attempt = 0) => {
      getServiceBySlug(slug)
        .then((data) => {
          if (!cancelled) setService(data);
        })
        .catch((err) => {
          if (cancelled) return;
          // A genuine 404 means the service truly doesn't exist. Anything
          // else (network blip, brief backend restart) is transient — retry
          // a couple of times before giving up, so a momentary hiccup never
          // looks like a missing service.
          if (err?.response?.status === 404) {
            setNotFound(true);
            return;
          }
          if (attempt < 2) {
            setTimeout(() => load(attempt + 1), 800 * (attempt + 1));
          } else {
            setNotFound(true);
          }
        });
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (notFound) return <NotFound />;

  if (!service) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-24 px-4 max-w-4xl mx-auto space-y-4">
        <Skeleton className="h-64 w-full rounded-2xl bg-[#141416]" />
        <Skeleton className="h-6 w-2/3 bg-[#141416]" />
        <Skeleton className="h-4 w-1/2 bg-[#141416]" />
      </div>
    );
  }

  const Icon = getIcon(service.icon_name);

  return (
    <div data-testid={SERVICE_DETAIL_PAGE.root}>
      <SEO title={service.name} description={service.short_description} image={service.hero_image_url} />
      <PageHero
        kicker="Service"
        title={service.name.toUpperCase()}
        subtitle={service.short_description}
        backgroundImage={service.hero_image_url}
        backgroundVideo={service.hero_video_url}
        height="min-h-[50vh] lg:min-h-[56vh]"
      />

      <section className="bg-[#0A0A0A]">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-10 py-14">
          <Link
            to="/services"
            data-testid={SERVICE_DETAIL_PAGE.backLink}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] transition-colors mb-10"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Services
          </Link>

          <Reveal>
            <div className="flex items-center gap-4 mb-6">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))]">
                <Icon className="h-5 w-5" />
              </span>
              <h2 className="font-display text-2xl tracking-[0.04em] text-[hsl(var(--lxra-fg))]">OVERVIEW</h2>
            </div>
            <p className="text-sm sm:text-base text-[hsl(var(--lxra-muted))] leading-relaxed max-w-3xl">
              {service.description}
            </p>
          </Reveal>

          {service.gallery_images?.length > 0 && (
            <Reveal delay={0.1}>
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {keyedList(service.gallery_images).map(({ key, item: img }) => (
                  <div key={key} className="aspect-square overflow-hidden rounded-xl border border-[hsl(var(--lxra-border))]">
                    <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />
                  </div>
                ))}
              </div>
            </Reveal>
          )}

          {/* PRICING */}
          <div className="mt-16" data-testid={SERVICE_DETAIL_PAGE.pricingSection}>
            <Reveal>
              <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))] mb-2">Investment</p>
              <h2 className="font-display text-3xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mb-8">PRICING PACKAGES</h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {service.pricing_packages.map((pkg, i) => (
                <Reveal key={pkg.name} delay={i * 0.08}>
                  <div
                    className={`relative flex flex-col rounded-2xl border p-6 h-full ${
                      pkg.highlighted
                        ? 'border-[hsl(var(--lxra-gold))] shadow-[0_0_0_1px_rgba(201,164,92,0.35),0_18px_40px_rgba(0,0,0,0.45)] bg-[#141416]'
                        : 'border-[hsl(var(--lxra-border))] bg-[#0F0F10]'
                    }`}
                  >
                    {pkg.highlighted && (
                      <span className="absolute -top-3 left-6 rounded-full bg-[hsl(var(--lxra-gold))] px-3 py-1 text-[10px] uppercase tracking-[0.1em] text-[#0A0A0A] font-semibold">
                        Most Popular
                      </span>
                    )}
                    <h3 className="font-display text-xl tracking-[0.04em] text-[hsl(var(--lxra-fg))]">{pkg.name}</h3>
                    <p className="mt-2 text-3xl font-display text-[hsl(var(--lxra-gold))]">{pkg.price}</p>
                    <p className="mt-1 text-xs text-[hsl(var(--lxra-muted))]">{pkg.duration}</p>
                    <ul className="mt-5 space-y-2.5 flex-1">
                      {pkg.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-[hsl(var(--lxra-muted))]">
                          <Check className="h-4 w-4 text-[hsl(var(--lxra-gold))] flex-shrink-0 mt-0.5" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={`/book?service=${service.slug}&package=${pkg.id}`} className="mt-6">
                      <button
                        data-testid={i === 0 ? SERVICE_DETAIL_PAGE.bookingButton : `${SERVICE_DETAIL_PAGE.bookingButton}-${i}`}
                        className={`w-full h-11 rounded-xl text-xs uppercase tracking-[0.1em] font-semibold transition-transform duration-200 hover:-translate-y-0.5 ${
                          pkg.highlighted
                            ? 'bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))]'
                            : 'border border-[hsl(var(--lxra-gold))]/50 text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] bg-transparent'
                        }`}
                      >
                        Book Now
                      </button>
                    </Link>
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="mt-4 text-xs text-[hsl(var(--lxra-muted))]">
              Pricing shown is a starting guide and may vary based on location, travel, and add-ons.
            </p>

            {/* LXRA Intelligence entry point */}
            <Reveal>
              <div
                className="mt-10 rounded-2xl border border-[hsl(var(--lxra-gold))]/30 bg-[#0F0F10] p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-6"
                data-testid="service-detail-intelligence-panel"
              >
                <div>
                  <p className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[hsl(var(--lxra-gold))]">
                    <Sparkles className="h-4 w-4" /> LXRA Intelligence
                    {!AI_FEATURES_ENABLED && (
                      <span className="rounded-full border border-[hsl(var(--lxra-gold))]/30 bg-[hsl(var(--lxra-gold))]/10 px-2 py-0.5 text-[10px] tracking-[0.1em]">
                        Coming Soon
                      </span>
                    )}
                  </p>
                  <h3 className="font-display text-2xl sm:text-3xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mt-2">
                    NOT SURE WHERE TO START?
                  </h3>
                  <p className="mt-2 max-w-lg text-sm text-[hsl(var(--lxra-muted))] leading-relaxed">
                    Answer a few questions and receive a Creative Blueprint for your {service.name.toLowerCase()} session —
                    direction, shot list, lighting, and locations — reviewed by your photographer.
                  </p>
                </div>
                <Link
                  to={`/plan?service=${service.slug}`}
                  data-testid={INTELLIGENCE_ENTRY.serviceDetailCta}
                  className="inline-flex items-center justify-center h-12 px-6 rounded-xl border border-[hsl(var(--lxra-gold))]/60 text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200 text-xs uppercase tracking-[0.1em] font-semibold whitespace-nowrap"
                >
                  Plan This Session
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>

          {/* FAQ */}
          {service.faqs?.length > 0 && (
            <div className="mt-16" data-testid={SERVICE_DETAIL_PAGE.faqSection}>
              <Reveal>
                <p className="text-xs tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))] mb-2">Good to Know</p>
                <h2 className="font-display text-3xl tracking-[0.03em] text-[hsl(var(--lxra-fg))] mb-4">
                  FREQUENTLY ASKED QUESTIONS
                </h2>
              </Reveal>
              <Accordion type="single" collapsible className="border-t border-[hsl(var(--lxra-border))]">
                {service.faqs.map((faq, i) => (
                  <AccordionItem key={faq.question} value={`item-${i}`} className="border-[hsl(var(--lxra-border))]">
                    <AccordionTrigger className="text-[hsl(var(--lxra-fg))] hover:text-[hsl(var(--lxra-gold))] hover:no-underline text-sm sm:text-base font-medium">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-[hsl(var(--lxra-muted))] text-sm leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}
        </div>
      </section>

      <CTASection
        kicker="Ready to book your session?"
        title="LET'S CREATE SOMETHING AMAZING."
        testId="service-detail-cta-banner"
        buttonTestId="service-detail-cta-book-now-button"
      />
    </div>
  );
}
